import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  Calculator, 
  TrendingUp,
  Wallet,
  PiggyBank,
  Home,
  Minus,
  Info,
  Building,
  Sigma
} from 'lucide-react';

// Provisionsstaffelung basierend auf wöchentlichen Einheiten
const PROVISION_FACTORS = [
  { maxUnits: 100, factor: 6.0 },
  { maxUnits: 200, factor: 6.5 },
  { maxUnits: 300, factor: 7.0 },
  { maxUnits: 350, factor: 7.5 },
  { maxUnits: 400, factor: 8.0 },
  { maxUnits: 500, factor: 8.5 },
  { maxUnits: 800, factor: 9.0 },
  { maxUnits: 1100, factor: 9.5 },
  { maxUnits: Infinity, factor: 10.0 }
];

// Wöchentliche Fixkosten
const UNTERKUNFT_PRO_WOCHE = 350.00;
const INSTITUT_FAKTOR = 2.0;
const MWST_RATE = 0.19;

const getFactor = (units) => {
  for (const tier of PROVISION_FACTORS) {
    if (units <= tier.maxUnits) return tier.factor;
  }
  return 10.0;
};

// Berechnung für eine Woche (jede Woche separat, gleiche Rechnung)
const calcWeek = (rawUnits) => {
  const units = parseFloat(rawUnits) || 0;
  const factor = getFactor(units);
  const wochenprovision = units * factor;
  const vorschuss70 = wochenprovision * 0.7;
  const stornoruecklage30 = wochenprovision * 0.3;
  const institutsgebuehrNetto = units * INSTITUT_FAKTOR;
  const institutsgebuehrBrutto = institutsgebuehrNetto * (1 + MWST_RATE);
  const unterkunft = UNTERKUNFT_PRO_WOCHE;
  const gesamtAbzuege = unterkunft + institutsgebuehrBrutto;
  const wochenverdienstSofort = vorschuss70 - gesamtAbzuege;
  return { units, factor, wochenprovision, vorschuss70, stornoruecklage30, institutsgebuehrBrutto, unterkunft, gesamtAbzuege, wochenverdienstSofort };
};

const formatCurrency = (value) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);

const WeekResult = ({ calc, label }) => (
  <div className="space-y-3">
    {label && (
      <div className="flex items-center gap-2">
        <Badge className="bg-[#1c2e52] text-white">{label}</Badge>
        <Badge variant="outline" className="bg-white" data-testid={`factor-badge-${label ? label.replace(/\s/g,'-').toLowerCase() : 'w'}`}>
          {calc.units} Einheiten → Faktor {calc.factor.toFixed(1)}
        </Badge>
      </div>
    )}

    {/* Wochenprovision */}
    <Card className="border-green-200 bg-green-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-green-700">
          <TrendingUp className="w-4 h-4" />
          Wöchentliche Provision (Brutto)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between items-center">
          <span>{calc.units} × {calc.factor.toFixed(1)} =</span>
          <span className="text-2xl font-bold text-green-700" data-testid="weekly-provision">{formatCurrency(calc.wochenprovision)}</span>
        </div>
      </CardContent>
    </Card>

    {/* Aufteilung */}
    <div className="grid grid-cols-2 gap-4">
      <Card className="border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-blue-700 mb-2">
            <Wallet className="w-4 h-4" />
            <span className="font-medium">70% Vorschuss</span>
          </div>
          <p className="text-xs text-gray-500 mb-1">Auszahlung nach Kampagne</p>
          <p className="text-xl font-bold text-blue-700" data-testid="vorschuss-70">{formatCurrency(calc.vorschuss70)}</p>
        </CardContent>
      </Card>
      <Card className="border-amber-200">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-amber-700 mb-2">
            <PiggyBank className="w-4 h-4" />
            <span className="font-medium">30% Stornorücklage</span>
          </div>
          <p className="text-xs text-gray-500 mb-1">Auszahlung nach 2 Jahren</p>
          <p className="text-xl font-bold text-amber-700" data-testid="stornoruecklage-30">{formatCurrency(calc.stornoruecklage30)}</p>
        </CardContent>
      </Card>
    </div>

    {/* Abzüge */}
    <Card className="border-red-200 bg-red-50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-red-700">
          <Minus className="w-4 h-4" />
          Abzüge vom 70% Vorschuss
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex justify-between items-center">
          <span className="flex items-center gap-2">
            <Home className="w-4 h-4 text-red-600" />
            Unterkunft pro Woche:
          </span>
          <span className="font-bold text-red-600" data-testid="unterkunft-abzug">-{formatCurrency(calc.unterkunft)}</span>
        </div>
        <div className="flex justify-between items-center border-t pt-2">
          <span className="flex items-center gap-2">
            <Building className="w-4 h-4 text-red-600" />
            <span>Institutsgebühr ({calc.units} × 2,0 + 19% MwSt):</span>
          </span>
          <span className="font-bold text-red-600" data-testid="institut-abzug">-{formatCurrency(calc.institutsgebuehrBrutto)}</span>
        </div>
        <div className="flex justify-between items-center border-t pt-2 font-medium">
          <span>Gesamt Abzüge:</span>
          <span className="text-red-700">-{formatCurrency(calc.gesamtAbzuege)}</span>
        </div>
      </CardContent>
    </Card>

    {/* Wochenergebnis */}
    <Card className="border-[#1c2e52] bg-[#1c2e52] text-white">
      <CardContent className="pt-4">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-[#e6d7bf] text-sm">{label ? `${label}: Verdienst (sofort)` : 'Dein Wochenverdienst (sofort)'}</p>
            <p className={`text-3xl font-bold ${calc.wochenverdienstSofort >= 0 ? 'text-[#4b95d1]' : 'text-red-400'}`} data-testid="weekly-income">{formatCurrency(calc.wochenverdienstSofort)}</p>
            <p className="text-xs text-gray-400 mt-1">= 70% Vorschuss - Abzüge</p>
          </div>
          <div className="text-right">
            <p className="text-[#e6d7bf] text-sm">+ nach 2 Jahren</p>
            <p className="text-xl font-bold text-amber-400">{formatCurrency(calc.stornoruecklage30)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
);

const GehaltsrechnerTrainee = ({ open, onClose }) => {
  const [numWeeks, setNumWeeks] = useState(1);
  const [unitsWeek1, setUnitsWeek1] = useState('');
  const [unitsWeek2, setUnitsWeek2] = useState('');

  const calc1 = useMemo(() => calcWeek(unitsWeek1), [unitsWeek1]);
  const calc2 = useMemo(() => calcWeek(unitsWeek2), [unitsWeek2]);

  const totalSofort = calc1.wochenverdienstSofort + (numWeeks === 2 ? calc2.wochenverdienstSofort : 0);
  const totalRuecklage = calc1.stornoruecklage30 + (numWeeks === 2 ? calc2.stornoruecklage30 : 0);

  const switchWeeks = (n) => {
    setNumWeeks(n);
    if (n === 2 && !unitsWeek2 && unitsWeek1) setUnitsWeek2(unitsWeek1);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1c2e52]">
            <Calculator className="w-5 h-5 text-[#4b95d1]" />
            Trainee Gehaltsrechner
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Info */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <p className="text-sm text-blue-700 flex items-start gap-2">
                <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                Berechnung pro Woche inkl. Institutsgebühr, Unterkunft ({formatCurrency(UNTERKUNFT_PRO_WOCHE)}) und Stornorücklage. Bei 2 Wochen wird jede Woche separat mit der gleichen Rechnung berechnet.
              </p>
            </CardContent>
          </Card>

          {/* Wochen-Auswahl */}
          <div className="flex gap-2">
            <Button
              variant={numWeeks === 1 ? 'default' : 'outline'}
              size="sm"
              className={numWeeks === 1 ? 'bg-[#1c2e52] hover:bg-[#1c2e52]/90 flex-1' : 'flex-1'}
              onClick={() => switchWeeks(1)}
              data-testid="weeks-toggle-1"
            >
              1 Woche
            </Button>
            <Button
              variant={numWeeks === 2 ? 'default' : 'outline'}
              size="sm"
              className={numWeeks === 2 ? 'bg-[#1c2e52] hover:bg-[#1c2e52]/90 flex-1' : 'flex-1'}
              onClick={() => switchWeeks(2)}
              data-testid="weeks-toggle-2"
            >
              2 Wochen
            </Button>
          </div>

          {/* Eingabe */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Einheiten eingeben</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={numWeeks === 2 ? 'grid grid-cols-2 gap-3' : 'space-y-2'}>
                <div className="space-y-2">
                  <Label>{numWeeks === 2 ? 'Einheiten Woche 1' : 'Einheiten pro Woche'}</Label>
                  <Input
                    type="number"
                    placeholder="z.B. 300"
                    value={unitsWeek1}
                    onChange={(e) => setUnitsWeek1(e.target.value)}
                    data-testid="weekly-units-input"
                  />
                </div>
                {numWeeks === 2 && (
                  <div className="space-y-2">
                    <Label>Einheiten Woche 2</Label>
                    <Input
                      type="number"
                      placeholder="z.B. 300"
                      value={unitsWeek2}
                      onChange={(e) => setUnitsWeek2(e.target.value)}
                      data-testid="weekly-units-input-2"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Ergebnisse */}
          {numWeeks === 1 && calc1.units > 0 && (
            <>
              <div className="flex items-center justify-center gap-2 py-2">
                <Badge variant="outline" className="text-lg px-4 py-1 bg-[#1c2e52] text-white border-none" data-testid="factor-badge">
                  {calc1.units} Einheiten → Faktor {calc1.factor.toFixed(1)}
                </Badge>
              </div>
              <WeekResult calc={calc1} />
              <Card className="bg-gray-50">
                <CardContent className="pt-4">
                  <p className="text-xs text-gray-600 text-center">
                    <strong>Gesamtverdienst pro Woche:</strong> {formatCurrency(calc1.wochenverdienstSofort)} sofort + {formatCurrency(calc1.stornoruecklage30)} (nach 2 Jahren) = <strong>{formatCurrency(calc1.wochenverdienstSofort + calc1.stornoruecklage30)}</strong>
                  </p>
                </CardContent>
              </Card>
            </>
          )}

          {numWeeks === 2 && (calc1.units > 0 || calc2.units > 0) && (
            <>
              {calc1.units > 0 && <WeekResult calc={calc1} label="Woche 1" />}
              {calc2.units > 0 && (
                <div className="border-t-2 border-dashed border-gray-300 pt-4">
                  <WeekResult calc={calc2} label="Woche 2" />
                </div>
              )}

              {/* Gesamt-Summe beider Wochen */}
              <Card className="border-2 border-[#4b95d1] bg-gradient-to-r from-[#1c2e52] to-[#2a4370] text-white">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2 text-[#e6d7bf]">
                    <Sigma className="w-4 h-4" />
                    Gesamt (Woche 1 + Woche 2)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-[#e6d7bf] text-sm">Verdienst gesamt (sofort)</p>
                      <p className={`text-3xl font-bold ${totalSofort >= 0 ? 'text-[#4b95d1]' : 'text-red-400'}`} data-testid="total-income-2weeks">{formatCurrency(totalSofort)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[#e6d7bf] text-sm">+ nach 2 Jahren</p>
                      <p className="text-xl font-bold text-amber-400" data-testid="total-ruecklage-2weeks">{formatCurrency(totalRuecklage)}</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-300 text-center mt-3 border-t border-white/20 pt-2">
                    Gesamtverdienst: {formatCurrency(totalSofort)} sofort + {formatCurrency(totalRuecklage)} (nach 2 Jahren) = <strong>{formatCurrency(totalSofort + totalRuecklage)}</strong>
                  </p>
                </CardContent>
              </Card>
            </>
          )}

          {/* Provisionsstaffel */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Provisionsstaffel (Faktor)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 text-xs">
                {PROVISION_FACTORS.map((tier, idx) => (
                  <div key={`tier-${tier.maxUnits}-${tier.factor}`} className="bg-gray-50 rounded p-2 text-center">
                    <p className="text-gray-500">
                      {idx === PROVISION_FACTORS.length - 1 
                        ? `ab ${PROVISION_FACTORS[idx-1].maxUnits + 1}` 
                        : `bis ${tier.maxUnits}`}
                    </p>
                    <p className="font-bold text-[#1c2e52]">×{tier.factor.toFixed(1)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GehaltsrechnerTrainee;
